// src/supabaseClient.js
import { createClient } from "@supabase/supabase-js";
import { getNamespace } from "./utils/devNamespace";

const globalKey = "__mudo_supabase_singleton__";
const auditKey = "__mudo_supabase_env_audit_logged__";

function toOrigin(url) {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function assertValidSupabaseBrowserEnv() {
  const rawUrl = import.meta.env.VITE_SUPABASE_URL;
  const rawKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const url = typeof rawUrl === "string" ? rawUrl.trim() : "";
  const anonKey = typeof rawKey === "string" ? rawKey.trim() : "";

  const hasLogged = typeof globalThis !== "undefined" ? !!globalThis[auditKey] : false;
  if (!hasLogged && typeof globalThis !== "undefined" && import.meta.env.DEV) {
    globalThis[auditKey] = true;
    const origin = toOrigin(url);
    const appOrigin = typeof window !== "undefined" ? window.location.origin : null;
    // Dev-only diagnostics (no secrets)
    // eslint-disable-next-line no-console
    console.info("[supabase] client", {
      appOrigin,
      supabaseOrigin: origin,
      anonKeyPresent: !!anonKey,
      autoRefreshToken: false,
    });
  }

  if (!url) {
    throw new Error(
      "[supabase] Missing VITE_SUPABASE_URL. Configure it (must be an https://*.supabase.co URL) and rebuild/redeploy."
    );
  }

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(
      `[supabase] Invalid VITE_SUPABASE_URL: ${JSON.stringify(url)}. Must be a valid HTTP or HTTPS URL (include https://).`
    );
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error(
      `[supabase] Invalid VITE_SUPABASE_URL protocol: ${parsed.protocol}. Must be http or https.`
    );
  }

  if (!anonKey) {
    throw new Error(
      "[supabase] Missing VITE_SUPABASE_ANON_KEY. Configure it and rebuild/redeploy."
    );
  }

  return { url, anonKey };
}

// Validate at import time so failures happen immediately (before UI renders).
const { url: SUPABASE_URL, anonKey: SUPABASE_ANON_KEY } = assertValidSupabaseBrowserEnv();

export const SUPABASE_URL_ORIGIN = toOrigin(SUPABASE_URL);

const storageKey = "sb-mudo-global";

// PRE-FLIGHT: Clear any stale sessions from localStorage that might trigger automatic
// refresh attempts and cause CORS/522 errors. This runs BEFORE creating the client.
function clearStaleSessionIfNeeded() {
  if (typeof localStorage === "undefined") return;
  
  try {
    // Check if there's a stored session
    const storedAuth = localStorage.getItem(storageKey);
    if (!storedAuth) return;
    
    let parsed;
    try {
      parsed = JSON.parse(storedAuth);
    } catch {
      // Corrupted JSON - clear it
      localStorage.removeItem(storageKey);
      if (import.meta.env.DEV) {
        console.warn("[supabase] Cleared corrupted session from localStorage");
      }
      return;
    }
    
    // Check if the access token is expired or about to expire (within 5 minutes)
    const expiresAt = parsed?.expires_at;
    if (!expiresAt) return;
    
    const expiresAtMs = expiresAt * 1000;
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    // If token is expired or expires soon, clear it to prevent auto-refresh attempts
    if (expiresAtMs - now <= fiveMinutes) {
      localStorage.removeItem(storageKey);
      // Also clear any other Supabase auth keys
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("sb-")) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {}
      });
      
      if (import.meta.env.DEV) {
        console.warn("[supabase] Cleared expired/expiring session from localStorage to prevent CORS/522 errors", {
          expiresAt: new Date(expiresAtMs).toISOString(),
          expired: expiresAtMs < now,
        });
      }
    }
  } catch (err) {
    // Don't block client creation if this fails
    if (import.meta.env.DEV) {
      console.warn("[supabase] Failed to check/clear stale session", err);
    }
  }
}

// Clear stale sessions BEFORE creating the client
clearStaleSessionIfNeeded();

// Singleton to avoid multiple GoTrue clients in HMR contexts
const existing =
  typeof globalThis !== "undefined" ? globalThis[globalKey] : null;

const client =
  existing ||
  (SUPABASE_URL && SUPABASE_ANON_KEY
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: {
          persistSession: true,
          // On-demand auth: refresh only when explicitly requested.
          autoRefreshToken: false,
          detectSessionInUrl: true,
          storageKey,
        },
      })
    : null);

// keep singleton on globalThis
if (typeof globalThis !== "undefined") {
  globalThis[globalKey] = client;
}

// 🔍 DEBUG: expose in browser console so we can do tests like supabase.auth.getSession()
if (typeof window !== "undefined") {
  if (client) {
    window.supabase = client;
    
    // Diagnostic helper: test Supabase connectivity from browser console
    if (import.meta.env.DEV) {
      window.testSupabaseConnection = async function() {
        console.group("🔍 Supabase Connection Diagnostic");
        
        // Test 1: Check env vars
        console.log("1️⃣ Environment variables:");
        console.log("  VITE_SUPABASE_URL:", SUPABASE_URL);
        console.log("  VITE_SUPABASE_ANON_KEY present:", !!SUPABASE_ANON_KEY);
        console.log("  Origin:", SUPABASE_URL_ORIGIN);
        
        // Test 2: Check network
        console.log("\n2️⃣ Network status:");
        console.log("  navigator.onLine:", navigator.onLine);
        
        // Test 3: Try to reach Supabase REST API (public health check)
        console.log("\n3️⃣ Testing Supabase REST endpoint...");
        try {
          const restUrl = `${SUPABASE_URL}/rest/v1/`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(restUrl, {
            method: "HEAD",
            headers: {
              "apikey": SUPABASE_ANON_KEY,
            },
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          
          console.log("  ✅ REST API reachable:", response.status, response.statusText);
        } catch (err) {
          console.error("  ❌ REST API failed:", err.name, err.message);
          if (err.name === "AbortError") {
            console.error("     → Request timed out after 5s (network/DNS/firewall issue)");
          } else {
            console.error("     → Check: VPN, browser extensions, antivirus, DNS settings");
          }
        }
        
        // Test 4: Try to reach Supabase Auth API
        console.log("\n4️⃣ Testing Supabase Auth endpoint...");
        try {
          const authUrl = `${SUPABASE_URL}/auth/v1/health`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(authUrl, {
            method: "GET",
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          
          if (response.ok) {
            const data = await response.json();
            console.log("  ✅ Auth API reachable:", data);
          } else {
            console.warn("  ⚠️  Auth API returned:", response.status, response.statusText);
          }
        } catch (err) {
          console.error("  ❌ Auth API failed:", err.name, err.message);
          if (err.name === "AbortError") {
            console.error("     → Request timed out after 5s");
            console.error("     → This is why login fails with 522/CORS errors");
          }
          console.error("     → Possible causes:");
          console.error("       • Supabase project paused/deleted");
          console.error("       • Network issue (VPN, firewall, DNS)");
          console.error("       • Browser extension blocking request");
          console.error("       • Antivirus/security software");
        }
        
        console.log("\n5️⃣ Recommended actions:");
        console.log("  • Check Supabase dashboard: https://supabase.com/dashboard");
        console.log("  • Disable VPN/proxy temporarily");
        console.log("  • Try in incognito mode (no extensions)");
        console.log("  • Check browser console for blocked requests");
        console.log("  • Verify .env.local has correct VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY");
        
        console.groupEnd();
      };
      
      console.info("[supabase] 💡 Run testSupabaseConnection() to diagnose connectivity issues");
    }
  }
}

export const supabase = client;

export function hasSupabase() {
  return !!supabase;
}
